import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeToggleButton } from './ThemeToggleButton';
import { ThemeAwareLogo } from './ThemeAwareLogo';

interface HeaderProps {
  title: string;
  onMenuClick: () => void;
  collapsed?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ title, onMenuClick, collapsed = false }) => {
  const { user } = useAuth();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsNotificationsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <header className={`fixed top-0 right-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200/80 dark:border-gray-800 h-16 flex items-center px-4 md:px-6 shadow-sm transition-all duration-300 left-0 ${collapsed ? 'md:left-20' : 'md:left-64'}`}>
      {/* Hamburger menu */}
      <button
        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
        aria-label="Toggle sidebar"
        onClick={onMenuClick}
        style={{ minWidth: 40, minHeight: 40 }}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      {/* Mobile brand indicator */}
      <div className="flex md:hidden items-center ml-2">
        <ThemeAwareLogo size="sm" showText={false} />
      </div>

      {/* Page Title */}
      <div className="ml-3 sm:ml-4">
        <h1 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[160px] sm:max-w-xs md:max-w-md">
          {title}
        </h1>
      </div>
      
      <div className="flex-1" />
      <div className="flex items-center space-x-2 sm:space-x-4">
        {user?.intCoinsBalance !== undefined && (
          <div className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
            <span className="mr-1">🪙</span> {(user.intCoinsBalance ?? 0).toLocaleString()} Coins
          </div>
        )}
        <span className="hidden md:inline-block text-gray-600 dark:text-gray-300 text-sm font-medium">
          Welcome, {user?.firstName || 'User'}
        </span>
        {/* Dark mode toggle */}
        <ThemeToggleButton />
        {/* Notification bell */}
        <div className="relative" ref={notifRef}>
          <button
            className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Notifications"
            title="Notifications"
            style={{ minWidth: 40, minHeight: 40 }}
            onClick={() => setIsNotificationsOpen(v => !v)}
          >
            <svg className="h-6 w-6 text-gray-500 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span className="absolute top-1 right-1 inline-block w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          {isNotificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-md shadow-lg z-50">
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200">
                Notifications
              </div>
              <div className="max-h-80 overflow-y-auto">
                <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
                  No notifications
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}; 